import { Box, Stack, Text } from '@chakra-ui/react';
import { IconButton } from '@chakra-ui/react';
import { Document } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { DOCUMENT_TYPES_REQUIRING_APPROVAL, getPrettyDocumentType } from '@pelicargo/utils';
import { useMemo } from 'react';
import {
  HiCheck,
  HiCheckCircle,
  HiOutlineArrowDownTray,
  HiOutlineCheckCircle,
  HiOutlineDocument,
} from 'react-icons/hi2';

import { useDownloadDocument } from '../../hooks/documents/useDownloadDocument';
import { useRequest } from '../../hooks/request/useRequest';

type Props = {
  document: Partial<Document>;
  label?: string;
};

export const DocumentRow = ({ document, label }: Props) => {
  const { download, isDownloading } = useDownloadDocument();
  const [request] = useRequest(document.request_id);

  const isUploaded = useMemo(() => !!document?.id, [document?.id]);
  const isRequestOwnedByDocumentAccount = useMemo(
    () => request?.account_id && document?.account_id && request.account_id === document.account_id,
    [request?.account_id, document?.account_id],
  );
  const requiresApproval = useMemo(
    () => !isRequestOwnedByDocumentAccount && DOCUMENT_TYPES_REQUIRING_APPROVAL.includes(document.type),
    [document.type, isRequestOwnedByDocumentAccount],
  );
  const isApproved = useMemo(() => !!document?.approved_at, [document?.approved_at]);

  return (
    <Stack key={document?.id || document?.type} direction="row" justify="space-between" align="center">
      <Stack direction="row" align="center" spacing="2">
        <Show if={isUploaded && !requiresApproval}>
          <Box>
            <HiCheck color="green" title="Approval not required" />
          </Box>
        </Show>
        <Show if={isUploaded && requiresApproval && !isApproved}>
          <Box>
            <HiOutlineCheckCircle color="green" title="Awaiting approval" />
          </Box>
        </Show>
        <Show if={isUploaded && requiresApproval && isApproved}>
          <Box>
            <HiCheckCircle color="green" title="Approved" />
          </Box>
        </Show>
        <Show if={!isUploaded}>
          <Box>
            <HiOutlineDocument title="Not uploaded" />
          </Box>
        </Show>
        <Text fontWeight="semibold" whiteSpace="nowrap" textAlign="left">
          {label ?? (getPrettyDocumentType(document?.type) || 'Document')}
        </Text>
        <Text title={document?.original_filename} color="text.subtle" noOfLines={1} wordBreak="break-all">
          {document?.original_filename}
        </Text>
      </Stack>
      <Show if={isUploaded}>
        <IconButton
          icon={<HiOutlineArrowDownTray />}
          aria-label="Download Document"
          variant="ghost"
          size="sm"
          color="text.muted"
          isLoading={isDownloading}
          onClick={() => download(document.id)}
        />
      </Show>
    </Stack>
  );
};
